/**
 * Database Service Layer for ZyloBill Web App
 * 
 * Adapted from desktop app's PouchDB service for browser environment
 * Provides:
 * - Database lifecycle management
 * - Document CRUD operations with error handling
 * - Connection management for local (IndexedDB) and remote (CouchDB) databases
 * - Account-based database isolation
 * - Real-time sync with change listeners
 */

// Import only PouchDB core - no plugins to avoid bundling issues
import PouchDB from 'pouchdb-browser';

class DatabaseService {
  constructor() {
    this.localDB = null;
    this.remoteDB = null;
    this.syncHandler = null;
    this.accountId = null;
    this.userId = null;
    this.deviceId = this.getDeviceId();
    this.changeListeners = new Map(); // Map of type -> Set of callbacks
    this.syncStatusListeners = new Set(); // Set of sync status callbacks
    this.syncStatus = 'offline'; // 'syncing' | 'synced' | 'error' | 'offline'
    this.lastSyncTime = null;
    this.syncError = null;
    
    console.log('[Database Service] Initialized with device ID:', this.deviceId);
  }

  /**
   * Get or generate device ID for browser
   * @returns {string} Device ID
   */
  getDeviceId() {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `web_${this.generateUUID()}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  /**
   * Generate UUID v4
   * @returns {string} UUID
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Initialize database connection after login
   * @param {object} credentials - CouchDB credentials from login
   * @param {string} credentials.couchdbUrl - CouchDB server URL
   * @param {string} credentials.couchdbUsername - CouchDB username
   * @param {string} credentials.couchdbPassword - CouchDB password
   * @param {number} credentials.accountId - Local account ID
   * @param {number} credentials.userId - User ID
   * @returns {Promise<void>}
   */
  async initialize(credentials) {
    try {
      console.log('[Database Service] Initializing database connection...');
      
      this.accountId = credentials.accountId;
      this.userId = credentials.userId;
      
      // Create local database (IndexedDB)
      await this.createLocalDatabase();
      
      // Create remote database connection
      await this.createRemoteDatabase(credentials);
      
      // Start sync
      await this.startSync();
      
      console.log('[Database Service] Database initialized successfully');
    } catch (error) {
      console.error('[Database Service] Failed to initialize database:', error);
      throw new Error(`Failed to initialize database: ${error.message}`);
    }
  }

  /**
   * Create local database using IndexedDB
   * @returns {Promise<void>}
   */
  async createLocalDatabase() {
    try {
      // Use same database name format as backend: zylobill_account_{accountId}
      const dbName = `zylobill_account_${this.accountId}`;
      
      console.log(`[Database Service] Creating local database: ${dbName}`);
      
      // Create PouchDB instance with IndexedDB adapter (default in browser)
      this.localDB = new PouchDB(dbName, {
        auto_compaction: true,
        revs_limit: 100
      });
      
      // Create indexes for efficient querying
      await this.createIndexes();
      
      // Fix existing documents (one-time migrations)
      await this.fixMissingDeletedField();
      await this.fixProductTypeToItem();
      
      // Verify database creation
      const info = await this.localDB.info();
      console.log('[Database Service] Local database created:', {
        name: info.db_name,
        adapter: info.adapter,
        docs: info.doc_count
      });
      
      // Set up change listeners
      this.setupChangeListeners();
      
    } catch (error) {
      console.error('[Database Service] Failed to create local database:', error);
      throw error;
    }
  }

  /**
   * Create remote database connection to CouchDB
   * @param {object} credentials - CouchDB credentials
   * @returns {Promise<void>}
   */
  async createRemoteDatabase(credentials) {
    try {
      const { couchdbUrl, couchdbUsername, couchdbPassword } = credentials;
      
      console.log('[Database Service] CouchDB credentials received:', {
        url: couchdbUrl,
        username: couchdbUsername,
        hasPassword: !!couchdbPassword,
        accountId: this.accountId
      });
      
      // The backend returns the full database URL including the database name
      // Format: https://sync.zylobill.com/zylobill_account_23
      // We need to use this URL directly with authentication
      
      const url = new URL(couchdbUrl);
      url.username = encodeURIComponent(couchdbUsername);
      url.password = encodeURIComponent(couchdbPassword);
      
      console.log('[Database Service] Connecting to CouchDB:', {
        host: url.host,
        pathname: url.pathname,
        username: couchdbUsername
      });
      
      this.remoteDB = new PouchDB(url.href, {
        skip_setup: false,
        ajax: {
          timeout: 30000,
          cache: false,
          headers: {
            'Authorization': 'Basic ' + btoa(couchdbUsername + ':' + couchdbPassword)
          }
        }
      });
      
      // Verify remote connection
      const info = await this.remoteDB.info();
      console.log('[Database Service] Remote database connected:', {
        name: info.db_name,
        docs: info.doc_count
      });
      
    } catch (error) {
      console.error('[Database Service] Failed to connect to remote database:', error);
      throw error;
    }
  }

  /**
   * Create indexes for efficient querying (disabled - no find plugin)
   * @returns {Promise<void>}
   */
  async createIndexes() {
    // Indexes disabled - using allDocs instead of find plugin
    console.log('[Database Service] Indexes skipped (using allDocs)');
    return Promise.resolve();
  }

  /**
   * Fix existing documents missing 'deleted' field
   * @returns {Promise<void>}
   */
  async fixMissingDeletedField() {
    try {
      const allDocs = await this.localDB.allDocs({ include_docs: true });
      const updates = [];
      
      for (const row of allDocs.rows) {
        const doc = row.doc;
        
        // Skip design documents
        if (doc._id.startsWith('_design/')) continue;
        
        // Add deleted: false if missing
        if (doc.deleted === undefined && doc.type) {
          updates.push({
            ...doc,
            deleted: false
          });
        }
      }
      
      if (updates.length > 0) {
        await this.localDB.bulkDocs(updates);
        console.log(`[Database Service] Fixed ${updates.length} documents missing 'deleted' field`);
      }
    } catch (error) {
      console.warn('[Database Service] Warning fixing missing deleted field:', error.message);
    }
  }

  /**
   * Fix items with wrong type (product -> item)
   * @returns {Promise<void>}
   */
  async fixProductTypeToItem() {
    try {
      const allDocs = await this.localDB.allDocs({ include_docs: true });
      const updates = [];
      
      for (const row of allDocs.rows) {
        const doc = row.doc;
        
        // Skip design documents
        if (doc._id.startsWith('_design/')) continue;
        
        // Fix items: type 'product' -> 'item'
        if (doc.type === 'product' && doc._id.startsWith('item_')) {
          updates.push({
            ...doc,
            type: 'item'
          });
        }
        
        // Fix invoices: type 'goods' or 'service' -> 'invoice'
        if ((doc.type === 'goods' || doc.type === 'service') && doc._id.startsWith('invoice_')) {
          updates.push({
            ...doc,
            type: 'invoice',
            invoiceType: doc.type
          });
        }
      }
      
      if (updates.length > 0) {
        await this.localDB.bulkDocs(updates);
        console.log(`[Database Service] Fixed ${updates.length} documents with incorrect type`);
      }
    } catch (error) {
      console.warn('[Database Service] Warning fixing document types:', error.message);
    }
  }

  /**
   * Start bidirectional sync between local and remote databases
   * @returns {Promise<void>}
   */
  async startSync() {
    if (!this.localDB || !this.remoteDB) {
      throw new Error('Local or remote database not initialized');
    }

    console.log('[Database Service] Starting bidirectional sync...');

    // Update sync status to syncing
    this.updateSyncStatus('syncing', null);

    // Start continuous bidirectional sync
    this.syncHandler = this.localDB.sync(this.remoteDB, {
      live: true,
      retry: true,
      back_off_function: (delay) => Math.min(delay * 2, 60000),
      batch_size: 100,
      batches_limit: 10
    });

    // Set up sync event handlers
    this.syncHandler
      .on('change', (info) => {
        console.log('[Database Service] Sync change:', {
          direction: info.direction,
          docsWritten: info.change?.docs_written || 0,
          pending: info.change?.pending || 0
        });
        
        // Update status to syncing when changes are being processed
        this.updateSyncStatus('syncing', null);
      })
      .on('paused', (err) => {
        if (err) {
          console.error('[Database Service] Sync paused with error:', err);
          this.updateSyncStatus('error', err);
        } else {
          console.log('[Database Service] Sync paused (up to date)');
          // Update status to synced when paused without error
          this.updateSyncStatus('synced', null);
          this.lastSyncTime = new Date();
        }
      })
      .on('active', () => {
        console.log('[Database Service] Sync active (syncing)');
        this.updateSyncStatus('syncing', null);
      })
      .on('denied', (err) => {
        console.error('[Database Service] Sync denied:', err);
        this.updateSyncStatus('error', err);
      })
      .on('error', (err) => {
        console.error('[Database Service] Sync error:', err);
        this.updateSyncStatus('error', err);
        // Emit error event for UI to handle
        this.emitSyncError(err);
      });

    console.log('[Database Service] Sync started successfully');
  }

  /**
   * Set up change listeners for real-time updates
   * @returns {void}
   */
  setupChangeListeners() {
    if (!this.localDB) return;

    this.localDB.changes({
      since: 'now',
      live: true,
      include_docs: true
    }).on('change', (change) => {
      if (change.deleted) {
        console.log('[Database Service] Document deleted:', change.id);
      } else {
        const doc = change.doc;
        const docType = doc.type;
        
        // Notify listeners for this document type
        if (this.changeListeners.has(docType)) {
          const listeners = this.changeListeners.get(docType);
          listeners.forEach(callback => {
            try {
              callback(doc);
            } catch (error) {
              console.error('[Database Service] Error in change listener:', error);
            }
          });
        }
      }
    });
  }

  /**
   * Subscribe to changes for a specific document type
   * @param {string} docType - Document type (e.g., 'invoice', 'client', 'item')
   * @param {Function} callback - Callback function to call when document changes
   * @returns {Function} Unsubscribe function
   */
  onDocumentChange(docType, callback) {
    if (!this.changeListeners.has(docType)) {
      this.changeListeners.set(docType, new Set());
    }
    
    this.changeListeners.get(docType).add(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.changeListeners.get(docType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Emit sync error event
   * @param {Error} error - Sync error
   */
  emitSyncError(error) {
    // Dispatch custom event for UI to listen to
    window.dispatchEvent(new CustomEvent('sync-error', { detail: error }));
  }

  /**
   * Sync Status Management
   */

  /**
   * Update sync status and notify listeners
   * @param {string} status - New sync status ('syncing' | 'synced' | 'error' | 'offline')
   * @param {Error|null} error - Error object if status is 'error'
   */
  updateSyncStatus(status, error = null) {
    const oldStatus = this.syncStatus;
    this.syncStatus = status;
    this.syncError = error;

    // Log status change
    if (oldStatus !== status) {
      console.log(`[Database Service] Sync status changed: ${oldStatus} -> ${status}`);
    }

    // Notify all sync status listeners
    this.syncStatusListeners.forEach(callback => {
      try {
        callback({
          status: this.syncStatus,
          lastSyncTime: this.lastSyncTime,
          error: this.syncError
        });
      } catch (err) {
        console.error('[Database Service] Error in sync status listener:', err);
      }
    });

    // Dispatch custom event for components that prefer event listeners
    window.dispatchEvent(new CustomEvent('sync-status-change', {
      detail: {
        status: this.syncStatus,
        lastSyncTime: this.lastSyncTime,
        error: this.syncError
      }
    }));
  }

  /**
   * Subscribe to sync status changes
   * @param {Function} callback - Callback function to call when sync status changes
   * @returns {Function} Unsubscribe function
   */
  onSyncStatusChange(callback) {
    this.syncStatusListeners.add(callback);

    // Immediately call with current status
    callback({
      status: this.syncStatus,
      lastSyncTime: this.lastSyncTime,
      error: this.syncError
    });

    // Return unsubscribe function
    return () => {
      this.syncStatusListeners.delete(callback);
    };
  }

  /**
   * Get current sync status
   * @returns {Object} Current sync status
   */
  getSyncStatus() {
    return {
      status: this.syncStatus,
      lastSyncTime: this.lastSyncTime,
      error: this.syncError
    };
  }

  /**
   * Document CRUD Operations
   */

  /**
   * Create document
   * @param {object} doc - Document to create (must include _id and type)
   * @returns {Promise<object>} Created document result
   */
  async createDocument(doc) {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      // Add metadata
      const docWithMetadata = {
        ...doc,
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: this.deviceId,
          updatedBy: this.deviceId,
          accountId: this.accountId,
          ...doc.metadata
        }
      };

      // Validate required fields
      if (!docWithMetadata._id) {
        throw new Error('Document must have an _id field');
      }

      const result = await this.localDB.put(docWithMetadata);
      
      console.log('[Database Service] Document created:', {
        id: result.id,
        rev: result.rev,
        type: docWithMetadata.type
      });

      return {
        id: result.id,
        rev: result.rev,
        ok: result.ok
      };
    } catch (error) {
      console.error('[Database Service] Failed to create document:', error);
      throw new Error(`Failed to create document: ${error.message}`);
    }
  }

  /**
   * Read document by ID
   * @param {string} docId - Document ID
   * @returns {Promise<object|null>} Document or null if not found
   */
  async readDocument(docId) {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      const doc = await this.localDB.get(docId);
      
      console.log('[Database Service] Document read:', {
        id: doc._id,
        rev: doc._rev,
        type: doc.type
      });

      return doc;
    } catch (error) {
      if (error.status === 404) {
        console.log('[Database Service] Document not found:', docId);
        return null;
      }
      
      console.error('[Database Service] Failed to read document:', error);
      throw new Error(`Failed to read document: ${error.message}`);
    }
  }

  /**
   * Update document
   * @param {object} doc - Document to update (must include _id and _rev)
   * @returns {Promise<object>} Updated document result
   */
  async updateDocument(doc) {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      // Validate required fields
      if (!doc._id || !doc._rev) {
        throw new Error('Document must have _id and _rev fields for update');
      }

      // Update metadata
      const docWithMetadata = {
        ...doc,
        metadata: {
          ...doc.metadata,
          updatedAt: new Date().toISOString(),
          updatedBy: this.deviceId
        }
      };

      const result = await this.localDB.put(docWithMetadata);
      
      console.log('[Database Service] Document updated:', {
        id: result.id,
        rev: result.rev
      });

      return {
        id: result.id,
        rev: result.rev,
        ok: result.ok
      };
    } catch (error) {
      console.error('[Database Service] Failed to update document:', error);
      throw new Error(`Failed to update document: ${error.message}`);
    }
  }

  /**
   * Delete document (soft delete)
   * @param {string} docId - Document ID
   * @returns {Promise<object>} Delete result
   */
  async deleteDocument(docId) {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      // Get current document
      const currentDoc = await this.localDB.get(docId);
      
      // Soft delete by marking as deleted
      const deletedDoc = {
        ...currentDoc,
        deleted: true,
        metadata: {
          ...currentDoc.metadata,
          deletedAt: new Date().toISOString(),
          deletedBy: this.deviceId,
          updatedAt: new Date().toISOString(),
          updatedBy: this.deviceId
        }
      };

      const result = await this.localDB.put(deletedDoc);
      
      console.log('[Database Service] Document soft deleted:', {
        id: result.id,
        rev: result.rev
      });

      return {
        id: result.id,
        rev: result.rev,
        ok: result.ok
      };
    } catch (error) {
      console.error('[Database Service] Failed to delete document:', error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  /**
   * Query Operations
   */

  /**
   * Find documents using allDocs (without find plugin)
   * @param {object} selector - Selector object (simplified)
   * @param {object} options - Query options
   * @returns {Promise<Array>} Query results
   */
  async findDocuments(selector, options = {}) {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      // Use allDocs instead of find (no plugin required)
      const result = await this.localDB.allDocs({
        include_docs: true,
        limit: options.limit || 1000
      });
      
      // Filter results based on selector
      let docs = result.rows
        .map(row => row.doc)
        .filter(doc => doc && !doc._id.startsWith('_design'));
      
      // Apply type filter if specified
      if (selector.type) {
        docs = docs.filter(doc => doc.type === selector.type);
      }
      
      // Apply deleted filter
      if (selector.deleted && selector.deleted.$ne !== undefined) {
        docs = docs.filter(doc => doc.deleted !== selector.deleted.$ne);
      }
      
      console.log('[Database Service] Query executed:', {
        found: docs.length,
        type: selector.type
      });

      return docs;
    } catch (error) {
      console.error('[Database Service] Query failed:', error);
      throw new Error(`Query failed: ${error.message}`);
    }
  }

  /**
   * Get all documents of a specific type
   * @param {string} docType - Document type
   * @param {object} options - Query options
   * @returns {Promise<Array>} Documents
   */
  async getDocumentsByType(docType, options = {}) {
    try {
      const selector = {
        type: docType,
        deleted: { $ne: true } // Exclude soft-deleted documents
      };

      const result = await this.findDocuments(selector, options);
      
      console.log('[Database Service] getDocumentsByType results:', {
        docType,
        count: result.length
      });
      
      return result;
    } catch (error) {
      console.error('[Database Service] Failed to get documents by type:', error);
      throw new Error(`Failed to get documents by type: ${error.message}`);
    }
  }

  /**
   * Convenience methods for specific document types
   */

  async getInvoices() {
    return this.getDocumentsByType('invoice');
  }

  async getClients() {
    return this.getDocumentsByType('client');
  }

  async getItems() {
    return this.getDocumentsByType('item');
  }

  async getQuotations() {
    return this.getDocumentsByType('quotation');
  }

  async getPayments() {
    return this.getDocumentsByType('payment');
  }

  /**
   * Duplicate a quotation
   * @param {string} quotationId - ID of quotation to duplicate
   * @returns {Promise<object>} New quotation document
   */
  async duplicateQuotation(quotationId) {
    try {
      // Get the original quotation
      const original = await this.readDocument(quotationId);
      
      if (!original) {
        throw new Error('Quotation not found');
      }

      if (original.type !== 'quotation') {
        throw new Error('Document is not a quotation');
      }

      // Generate new ID and quote number
      const newId = `quotation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date().toISOString();
      
      // Create duplicate with new ID and reset status
      const duplicate = {
        ...original,
        _id: newId,
        _rev: undefined, // Remove _rev for new document
        quote_number: `${original.quote_number}-COPY`,
        status: 'draft',
        metadata: {
          createdAt: timestamp,
          updatedAt: timestamp,
          createdBy: this.deviceId,
          updatedBy: this.deviceId,
          accountId: this.accountId
        }
      };

      // Create the new document
      await this.createDocument(duplicate);
      
      console.log('[Database Service] Quotation duplicated:', {
        originalId: quotationId,
        newId: newId,
        quoteNumber: duplicate.quote_number
      });

      return duplicate;
    } catch (error) {
      console.error('[Database Service] Failed to duplicate quotation:', error);
      throw new Error(`Failed to duplicate quotation: ${error.message}`);
    }
  }

  async getSettings() {
    return this.getDocumentsByType('settings');
  }

  /**
   * Database Information
   */

  /**
   * Get database information
   * @returns {Promise<object>} Database info
   */
  async getDatabaseInfo() {
    try {
      if (!this.localDB) {
        throw new Error('Database not initialized');
      }

      const info = await this.localDB.info();
      
      console.log('[Database Service] Database info:', {
        docs: info.doc_count,
        updateSeq: info.update_seq,
        adapter: info.adapter
      });

      return info;
    } catch (error) {
      console.error('[Database Service] Failed to get database info:', error);
      throw error;
    }
  }

  /**
   * Cleanup
   */

  /**
   * Stop sync and close connections
   * @returns {Promise<void>}
   */
  async cleanup() {
    try {
      console.log('[Database Service] Cleaning up...');

      // Update sync status to offline
      this.updateSyncStatus('offline', null);

      // Cancel sync
      if (this.syncHandler) {
        this.syncHandler.cancel();
        this.syncHandler = null;
      }

      // Close databases
      if (this.localDB) {
        await this.localDB.close();
        this.localDB = null;
      }

      if (this.remoteDB) {
        await this.remoteDB.close();
        this.remoteDB = null;
      }

      // Clear listeners
      this.changeListeners.clear();
      this.syncStatusListeners.clear();

      console.log('[Database Service] Cleanup complete');
    } catch (error) {
      console.error('[Database Service] Cleanup error:', error);
    }
  }
}

// Create singleton instance
const databaseService = new DatabaseService();

export default databaseService;
